import { useEffect, useState } from "react";
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowRightIcon, ArrowUturnLeftIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, InboxIcon, PhotoIcon, PlayIcon } from "@heroicons/react/20/solid";
import { useDataset } from "../../../contexts/DatasetContext";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { Panel } from "../../../components/panels/Panel";
import { ConfirmModal } from "../../../components/modals/ConfirmModal";
import { AnnotationImageEditor } from "./AnnotationImageEditor";
import { ZoomableImage } from "./annotations/ZoomableImage";

export const AnnotationPageContent = ({ compactLeftSpacing=false }) => {
  const {
    selectedDataset,
    selectedImage,
    selectedImageId,
    isStartingDataset,
    isSavingImage,
    selectImage,
    startDataset,
    validateImage,
    requestSkipImage,
    setHasUnsavedChanges,
    isCompletingDataset,
    completeDataset,
    requestMarkImageAsUnseen,
  } = useDataset();

  if (selectedDataset === null)
    return <AnnotationPageEmptyState Icon={InboxIcon} title="Aucun dossier sélectionné" text="Sélectionnez un dossier dans la liste pour afficher son contenu." />;

  if (selectedDataset.state === 'waiting_capture')
    return <AnnotationPageWaitingCapture dataset={selectedDataset} isStarting={isStartingDataset} onStart={() => startDataset(selectedDataset.id)} />;

  if (selectedDataset.state === 'in_progress' || selectedDataset.state === 'completed') {
    return <AnnotationPageWorkspace dataset={selectedDataset} selectedImage={selectedImage} selectedImageId={selectedImageId} isSavingImage={isSavingImage}
                                    isCompletingDataset={isCompletingDataset} readOnly={selectedDataset.state === 'completed'} onSelectImage={selectImage}
                                    onValidateImage={validateImage} onSkipImage={requestSkipImage} onMarkImageAsUnseen={requestMarkImageAsUnseen}
                                    onUnsavedChangesChange={setHasUnsavedChanges} onCompleteDataset={completeDataset} compactLeftSpacing={compactLeftSpacing} />;
  }

  return null;
};

const AnnotationPageEmptyState = ({ Icon, title, text }) => {
  return (
    <div className="h-full p-4">
      <Panel noHeader rounded category="secondary">
        <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
          <Icon className="size-24 text-perception-gray-700" />

          <div>
            <div className="text-white text-xl font-semibold">{title}</div>
            <div className="mt-2 text-perception-gray-500">{text}</div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

const AnnotationPageWaitingCapture = ({ dataset, isStarting, onStart }) => {
  const sortedImages = [...(dataset.dataset_images ?? [])].sort((imageA, imageB) => imageA.id - imageB.id);

  return (
    <div className="h-full flex flex-col p-4 gap-4">
      <div className="flex-1 min-h-0">
        <Panel noHeader rounded category="secondary">
          <div className="h-full flex flex-col items-center justify-center gap-6 text-center">
            <ClockIcon className="size-24 text-perception-gray-700" />

            <div>
              <div className="text-white text-xl font-semibold">{dataset.name}</div>
              <div className="mt-2 text-perception-gray-500">
                Capture en cours — {dataset.total_images} image{dataset.total_images > 1 ? 's' : ''} disponible{dataset.total_images > 1 ? 's' : ''}.
              </div>
            </div>

            <ActionButton text={isStarting ? 'Démarrage...' : 'Démarrer l’annotation'} Icon={PlayIcon} disabled={isStarting} onClick={onStart} />
          </div>
        </Panel>
      </div>

      <AnnotationPageThumbnails dataset={dataset} images={sortedImages} />
    </div>
  );
};

const ANNOTATION_TOLERANCE = 0.0001;

const areAnnotationsEqual = (automaticAnnotations, userAnnotations) => {
  if (automaticAnnotations.length !== userAnnotations.length) return false;

  const sortedAutomaticAnnotations = [...automaticAnnotations].sort((annotationA, annotationB) => String(annotationA.id).localeCompare(String(annotationB.id)));
  const sortedUserAnnotations = [...userAnnotations].sort((annotationA, annotationB) => String(annotationA.id).localeCompare(String(annotationB.id)));

  return sortedAutomaticAnnotations.every((automaticAnnotation, index) => {
    const userAnnotation = sortedUserAnnotations[index];

    if (automaticAnnotation.id !== userAnnotation.id) return false;

    return Math.abs(automaticAnnotation.x - userAnnotation.x) <= ANNOTATION_TOLERANCE &&
      Math.abs(automaticAnnotation.y - userAnnotation.y) <= ANNOTATION_TOLERANCE &&
      Math.abs(automaticAnnotation.width - userAnnotation.width) <= ANNOTATION_TOLERANCE &&
      Math.abs(automaticAnnotation.height - userAnnotation.height) <= ANNOTATION_TOLERANCE &&
      Math.abs((automaticAnnotation.angle ?? 0) - (userAnnotation.angle ?? 0)) <= ANNOTATION_TOLERANCE;
  });
};

const AnnotationPageWorkspace = ({
                                   dataset,
                                   selectedImage,
                                   selectedImageId,
                                   isSavingImage,
                                   isCompletingDataset,
                                   readOnly,
                                   onSelectImage,
                                   onValidateImage,
                                   onSkipImage,
                                   onMarkImageAsUnseen,
                                   onUnsavedChangesChange,
                                   onCompleteDataset,
                                   compactLeftSpacing,
                                 }) => {
  const sortedImages = [...(dataset.dataset_images ?? [])].sort((imageA, imageB) => imageA.id - imageB.id);
  const [workingAnnotations, setWorkingAnnotations] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const allImagesViewed = sortedImages.length > 0 && sortedImages.every((image) => image.state !== 'unseen');
  const validatedImages = sortedImages.filter((image) => image.state === 'validated');
  const skippedImages = sortedImages.filter((image) => image.state === 'skipped');

  const unchangedImages = validatedImages.filter((image) => {
    const automaticAnnotations = image.annotation_data?.auto ?? [];
    const userAnnotations = image.annotation_data?.user ?? [];
    return areAnnotationsEqual(automaticAnnotations, userAnnotations);
  });

  const changedImages = validatedImages.filter((image) => {
    const automaticAnnotations = image.annotation_data?.auto ?? [];
    const userAnnotations = image.annotation_data?.user ?? [];
    return !areAnnotationsEqual(automaticAnnotations, userAnnotations);
  });

  useEffect(() => {
    setWorkingAnnotations([]);
    setHasUnsavedChanges(false);
    onUnsavedChangesChange(false);
  }, [selectedImage?.id]);

  const handleAnnotationsChange = (annotations, hasChanges) => {
    setWorkingAnnotations(annotations);

    if (readOnly) {
      setHasUnsavedChanges(false);
      onUnsavedChangesChange(false);
      return;
    }

    setHasUnsavedChanges(hasChanges);
    onUnsavedChangesChange(hasChanges);
  };

  const handleValidateImage = () => {
    onValidateImage({
      auto: selectedImage.annotation_data?.auto ?? [],
      user: workingAnnotations,
    });
  };

  const handleCompleteDataset = async () => {
    try {
      await onCompleteDataset();
      setIsCompleteModalOpen(false);
    } catch (error) {
      // Error is already handled by DatasetContext.
    }
  };

  return (
    <>
      <ConfirmModal isOpen={isCompleteModalOpen} setIsOpen={setIsCompleteModalOpen} title="Terminer le dossier ?"
                    text="Le dossier passera en lecture seule et son résumé sera affiché." onConfirm={handleCompleteDataset} />

      <div className={`h-full flex py-3 pr-3 gap-3 overflow-hidden ${compactLeftSpacing ? 'pl-0' : 'pl-3'}`}>
        <AnnotationPageThumbnails dataset={dataset} images={sortedImages} selectedImageId={selectedImageId}
                                  disabled={isSavingImage || isCompletingDataset} onSelectImage={onSelectImage} vertical />

        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          {selectedImage === null && (allImagesViewed || readOnly) ? (
            <AnnotationPagePreSummary dataset={dataset} unchangedImages={unchangedImages.length} changedImages={changedImages.length}
                                      skippedImages={skippedImages.length} isCompleting={isCompletingDataset} completed={readOnly}
                                      onComplete={() => setIsCompleteModalOpen(true)} />
          ) : selectedImage === null ? (
            <Panel noHeader rounded category="secondary">
              <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                <PhotoIcon className="size-24 text-perception-gray-700" />

                <div>
                  <div className="text-white text-xl font-semibold">Aucune image disponible</div>
                  <div className="mt-2 text-perception-gray-500">Ce dossier ne contient aucune image à annoter.</div>
                </div>
              </div>
            </Panel>
          ) : (
            <div className="flex-1 min-h-0 min-w-0 grid grid-rows-[2fr_1fr] gap-3 overflow-hidden">
              <div key={selectedImage.id} className="min-h-0 min-w-0 flex gap-3 overflow-hidden animate-fade">
                <AnnotationPageEditorPanel dataset={dataset} image={selectedImage} disabled={isSavingImage || readOnly} onChange={handleAnnotationsChange} />

                <AnnotationPageImagePanel title="ANALYSE IA - CARTE DE CHALEUR"
                                          src={`${process.env.REACT_APP_FILE_URL}datasets/heatmaps/${selectedImage.id}.jpg`} />
              </div>

              <div className="min-h-0 min-w-0 flex gap-3">
                <div className="relative w-1/2 min-w-0 flex items-center justify-center">
                  {readOnly ? (
                    <ActionButton text="Retour au résumé" Icon={ArrowLeftIcon} color="secondary" onClick={() => onSelectImage(null)} />
                  ) : (
                    <div className="flex gap-4">
                      {['validated', 'skipped'].includes(selectedImage.state) ? (
                        <ActionButton text="Marquer comme non traitée" Icon={ArrowUturnLeftIcon} color="secondary" disabled={isSavingImage} onClick={onMarkImageAsUnseen} />
                      ) : (
                        <ActionButton text="Ignorer l’image" Icon={ArrowRightIcon} color="secondary" disabled={isSavingImage} onClick={onSkipImage} />
                      )}

                      <ActionButton text={isSavingImage ? 'Validation...' : 'Valider et continuer'} Icon={CheckCircleIcon}
                                    disabled={isSavingImage} onClick={handleValidateImage} />
                    </div>
                  )}

                  <div className="absolute right-0 top-0 text-xs text-perception-gray-700">
                    Molette : zoom · Alt + glisser : déplacer
                  </div>
                </div>

                <div className="w-1/2 min-w-0">
                  <AnnotationPageLocationPanel imageId={selectedImage.id} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const seededRandom = (seed, offset=0) => {
  const value = Math.sin((Number(seed) + 1) * 12.9898 + offset * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const getLocationRectangle = (imageId) => {
  const x = 360 + seededRandom(imageId, 1) * 650;
  const y = 210 + seededRandom(imageId, 2) * 430;
  const width = 70 + seededRandom(imageId, 3) * 25;
  const height = 50 + seededRandom(imageId, 4) * 20;

  return { x, y, width, height };
};

const AnnotationPageLocationPanel = ({ imageId }) => {
  const src = `${process.env.REACT_APP_FILE_URL}datasets/location.png`;
  const rectangle = getLocationRectangle(imageId);

  return (
    <div className="h-full min-h-0 overflow-hidden">
      <Panel title="LOCALISATION DE LA PIÈCE" category="secondary" rounded overflowAuto={false}>
        <div className="-m-4 h-[calc(100%+2rem)] w-[calc(100%+2rem)] min-h-0 min-w-0 overflow-hidden rounded-b-md bg-white">
          <svg className="h-full w-full" viewBox="0 0 1456 1054" preserveAspectRatio="xMidYMid meet">
            <image href={src} x="0" y="0" width="1456" height="1054" preserveAspectRatio="xMidYMid meet" />

            <rect x={rectangle.x} y={rectangle.y} width={rectangle.width} height={rectangle.height}
                  fill="none" stroke="red" strokeWidth="8" />
          </svg>
        </div>
      </Panel>
    </div>
  );
};

const AnnotationPagePreSummary = ({ dataset, unchangedImages, changedImages, skippedImages, isCompleting, completed, onComplete }) => {
  const handleDownloadReport = () => {
    const link = document.createElement('a');

    link.href = `${process.env.REACT_APP_FILE_URL}datasets/report.pdf`;
    link.download = 'report.pdf';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Panel noHeader rounded category="secondary">
      <div className="h-full flex flex-col items-center justify-center gap-8 text-center">
        <CheckCircleIcon className="size-24 text-perception-success-600" />

        <div>
          <div className="text-white text-2xl font-semibold">{dataset.name}</div>
          <div className="mt-2 text-perception-gray-500">
            {completed ? 'Ce dataset est terminé et disponible en lecture seule.' : 'Toutes les images ont été consultées.'}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 w-full max-w-3xl">
          <AnnotationSummaryCard label="Validées sans modification" value={unchangedImages} />
          <AnnotationSummaryCard label="Validées avec modification" value={changedImages} />
          <AnnotationSummaryCard label="Ignorées" value={skippedImages} />
        </div>

        {completed ? (
          <ActionButton text="Télécharger le rapport" Icon={ArrowDownTrayIcon} onClick={handleDownloadReport} />
        ) : (
          <ActionButton text={isCompleting ? 'Validation...' : 'Terminer le dossier'} Icon={CheckCircleIcon}
                        color="success" disabled={isCompleting} onClick={onComplete} />
        )}
      </div>
    </Panel>
  );
};

const AnnotationSummaryCard = ({ label, value }) => {
  return (
    <div className="rounded-md bg-perception-black-700 p-6">
      <div className="text-3xl font-semibold text-white">{value}</div>
      <div className="mt-2 text-sm text-perception-gray-500">{label}</div>
    </div>
  );
};

const AnnotationPageEditorPanel = ({ dataset, image, disabled, onChange }) => {
  return (
    <div className="h-full w-1/2 min-w-0 min-h-0 overflow-hidden">
      <Panel title="PHOTO ORIGINALE" category="secondary" rounded overflowAuto={false}>
        <div className="-m-4 h-[calc(100%+2rem)] w-[calc(100%+2rem)] min-h-0 min-w-0 overflow-hidden rounded-b-md">
          <AnnotationImageEditor key={image.id} src={`${process.env.REACT_APP_FILE_URL}datasets/${image.id}.jpg`}
                                 annotationData={image.annotation_data} disabled={disabled} onChange={onChange} />
        </div>
      </Panel>
    </div>
  );
};

const AnnotationPageImagePanel = ({ title, src }) => {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [src]);

  return (
    <div className="h-full w-1/2 min-w-0 min-h-0 overflow-hidden">
      <Panel title={title} category="secondary" rounded overflowAuto={false}>
        <div className="-m-4 h-[calc(100%+2rem)] w-[calc(100%+2rem)] min-h-0 min-w-0 overflow-hidden rounded-b-md bg-perception-black-700">
          {hasImageError ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
              <ExclamationTriangleIcon className="size-12 text-perception-yellow" />

              <div>
                <div className="font-semibold text-white">Carte de chaleur indisponible</div>
                <div className="mt-1 text-sm text-perception-gray-500">L’analyse est peut-être encore en cours de génération.</div>
              </div>
            </div>
          ) : (
            <ZoomableImage key={src} src={src} alt={title} onError={() => setHasImageError(true)} />
          )}
        </div>
      </Panel>
    </div>
  );
};

const AnnotationThumbnailImage = ({ src, alt }) => {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [src]);

  if (hasImageError) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-perception-black-600">
        <ExclamationTriangleIcon className="size-6 text-perception-yellow" />
      </div>
    );
  }

  return <img key={src} src={src} alt={alt} draggable={false} className="h-full w-full object-cover" onError={() => setHasImageError(true)} />;
};

const AnnotationPageThumbnails = ({ dataset, images, selectedImageId=null, disabled=false, onSelectImage=null, vertical=false }) => {
  if (vertical) {
    return (
      <div className="h-full w-40 shrink-0">
        <Panel noHeader rounded category="secondary">
          <div className="-m-3 h-[calc(100%+1.5rem)] p-1">
            {images.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-perception-gray-700 text-center">
                <PhotoIcon className="size-6" />
                <span className="text-xs">Aucune image</span>
              </div>
            ) : (
              <div className="h-full flex flex-col gap-1 overflow-y-auto">
                {images.map((image, index) => (
                  <AnnotationPageThumbnail key={image.id} dataset={dataset} image={image} displayId={index + 1} isSelected={selectedImageId === image.id}
                                           disabled={disabled} onClick={onSelectImage ? () => onSelectImage(image.id) : null} vertical />
                ))}
              </div>
            )}
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="h-40">
      <Panel noHeader rounded category="secondary">
        {images.length === 0 ? (
          <div className="h-full flex items-center justify-center gap-2 text-perception-gray-700">
            <PhotoIcon className="size-6" />
            <span>Aucune image disponible</span>
          </div>
        ) : (
          <div className="h-full flex gap-4 overflow-x-auto">
            {images.map((image, index) => (
              <AnnotationPageThumbnail key={image.id} dataset={dataset} image={image} displayId={index + 1} isSelected={selectedImageId === image.id}
                                       disabled={disabled} onClick={onSelectImage ? () => onSelectImage(image.id) : null} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

const AnnotationPageThumbnail = ({ dataset, image, displayId, isSelected, disabled, onClick, vertical=false }) => {
  const getStateBadge = () => {
    const stateClass =
      image.state === 'validated' ? 'bg-perception-success-600/80' :
        image.state === 'skipped' ? 'bg-yellow-500/80' :
          'bg-perception-gray-800/80';

    const stateText =
      image.state === 'validated' ? 'Validée' :
        image.state === 'skipped' ? 'Ignorée' :
          'À traiter';

    return <span className={`rounded-md backdrop-blur-md px-2 py-1 text-xs font-medium text-white ${stateClass}`}>{stateText}</span>;
  };

  return (
    <button type="button" disabled={disabled || onClick === null}
            className={`relative overflow-hidden rounded-md bg-perception-black-700 border-2 transition ${vertical ? 'w-full h-28 min-h-28' : 'h-full w-32 min-w-32'} ${
              isSelected ? 'border-perception-blue' : 'border-transparent'
            } ${onClick && !disabled ? 'cursor-pointer hover:border-perception-gray-700' : 'cursor-default'} ${disabled ? 'opacity-60' : ''}`}
            onClick={onClick ?? undefined}>
      <AnnotationThumbnailImage src={`${process.env.REACT_APP_FILE_URL}datasets/${image.id}.jpg`} alt={`Image ${displayId}`} />

      <div className="absolute inset-x-2 top-2 flex justify-between items-start gap-1">
        <span className="rounded-md backdrop-blur-md bg-perception-blue/80 px-2 py-1 text-xs font-medium text-white">#{displayId}</span>
        {getStateBadge()}
      </div>
    </button>
  );
};